import { capitalize, compact, pick, trim } from "lodash"
import PropTypes from "prop-types"
import { LONG, LONG_SHORT, SHORT } from "resources/position-sets/composition"
import { clampDate, getPreviousModeledDate } from "helpers/dates"

export const PORTFOLIO = "PORTFOLIO"
export const BASKET = "BASKET"
export const SWAP = "SWAP"
export const SECURITY = "SECURITY"
export const ETF = "ETF"
export const INDEX = "INDEX"
export const BENCHMARK = "BENCHMARK"
export const RULES = "RULES"

export const CONTRIBUTION = "CONTRIBUTION"
export const STANDALONE = "STANDALONE"

function getPositionSet(rawPositionSet, date, weight, fxHedgeEnabled) {
  if (!rawPositionSet) {
    return undefined
  }

  switch (rawPositionSet.type) {
    case BASKET:
      return { dates: [{ date, swaps: [{ id: rawPositionSet.id, economicExposure: weight }] }], expand: true, segment: rawPositionSet.segment, fxHedgeEnabled }
    case SECURITY:
      return { dates: [{ date, equities: [{ id: { internalId: rawPositionSet.id }, economicExposure: weight }] }], segment: rawPositionSet.segment, fxHedgeEnabled }
    case ETF:
      return { dates: [{ date, equities: [{ id: { internalId: rawPositionSet.id }, economicExposure: weight }] }], expand: true, segment: rawPositionSet.segment, fxHedgeEnabled }
    case INDEX:
      return { dates: [{ date, indexes: [{ id: rawPositionSet.id, economicExposure: weight }] }], expand: true, segment: rawPositionSet.segment, fxHedgeEnabled }
    case RULES:
      return { type: rawPositionSet.type, rulesConfiguration: rawPositionSet.rulesConfiguration, segment: rawPositionSet.segment, fxHedgeEnabled }
    default:
      return { type: rawPositionSet.type, id: rawPositionSet.id, segment: rawPositionSet.segment, fxHedgeEnabled }
  }
}

function getBasePositionSet(rawPositionSet, weight) {
  if (!rawPositionSet) {
    return undefined
  }

  return { ...pick(rawPositionSet, ["type", "id", "segment"]), weight }
}

function getFxHedgeEnabled(rawPositionSet, mainPositionSetFxHedgeEnabled) {
  if (rawPositionSet.fxHedgeEnabled !== undefined) {
    return rawPositionSet.fxHedgeEnabled
  }

  return mainPositionSetFxHedgeEnabled
}

export function parsePositionSet(rawPositionSet, positionSetWithBase, positionSetMetadata, baseMetadata, date, fixed, isContextActive, mainPositionSetFxHedgeEnabled, isPerformance, modelMetadata) {
  const evaluatedAsShort = rawPositionSet.asShort || false
  const segmentedBy = rawPositionSet?.segment?.filters?.longShort || null
  const segmentNormalization = rawPositionSet?.segment?.normalization || null
  const customBenchmark = !fixed && rawPositionSet.base
  const fxHedgeEnabled = getFxHedgeEnabled(rawPositionSet, mainPositionSetFxHedgeEnabled)

  const additionalHeaders = compact([
    evaluatedAsShort && "Evaluated as Short",
    segmentedBy && `${capitalize(segmentedBy)} Only - Normalized as ${capitalize(segmentNormalization)}`,
    isContextActive && !fixed && customBenchmark === false && "Absolute",
    !!customBenchmark && `Active to ${baseMetadata.name}`,
    fxHedgeEnabled && "FX Hedge Enabled",
  ])

  const weight = rawPositionSet.asShort ? -1 : 1
  let benchmarkWeight = 1
  if (rawPositionSet.asShort || segmentedBy === SHORT || positionSetMetadata.compositionType === SHORT) {
    benchmarkWeight = -1
  }

  let positionSetDate = date
  if (isPerformance) {
    const modelStartDate = modelMetadata?.availability?.securitiesStartDate
    const unmodeledDates = modelMetadata?.availability?.unmodeledDates || []
    const previousModeledDate = getPreviousModeledDate(date, modelStartDate, unmodeledDates)
    positionSetDate = clampDate(previousModeledDate, positionSetMetadata.earliestDate)
  }

  const unavailable = Boolean(positionSetMetadata?.unavailable)

  return {
    name: !unavailable ? (trim(positionSetMetadata.name) || rawPositionSet.id) : "",
    type: rawPositionSet.type,
    fixed,
    unavailable,
    evaluatedAsShort,
    normalization: segmentNormalization,
    longShort: positionSetMetadata.compositionType,
    queryStringPositionSet: rawPositionSet,
    queryPositionSet: getPositionSet(rawPositionSet, positionSetDate, weight, fxHedgeEnabled),
    queryBase: getBasePositionSet(positionSetWithBase?.base, benchmarkWeight),
    additionalHeaders: additionalHeaders.length ? additionalHeaders : null,
    fxHedgeEnabled,
    referenceEquity: positionSetMetadata.referenceEquity,
  }
}

const QUERY_POSITION_SET = PropTypes.object

export const POSITION_SET = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf([PORTFOLIO, BASKET, SECURITY, ETF, BENCHMARK, INDEX, RULES]).isRequired,
  fixed: PropTypes.bool.isRequired,
  unavailable: PropTypes.bool.isRequired,
  evaluatedAsShort: PropTypes.bool.isRequired,
  normalization: PropTypes.oneOf([CONTRIBUTION, STANDALONE]),
  longShort: PropTypes.oneOf([LONG, SHORT, LONG_SHORT]).isRequired,
  queryStringPositionSet: QUERY_POSITION_SET.isRequired,
  queryPositionSet: QUERY_POSITION_SET.isRequired,
  queryBase: QUERY_POSITION_SET.isRequired,
  additionalHeaders: PropTypes.arrayOf(PropTypes.string),
}
